/**
 * Feedback Component
 *
 * Feedback collection sidebar with rating and categorization.
 */

import { CheckCircle2, Lightbulb, Send, X } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import { useSubmitFeedback, useTrackEvent } from '@tonnie/sdk';
import { Button, cn, ScrollArea } from '@tonnie/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useLocation } from 'wouter';

import { useAuth } from '@/core/contexts/AuthContext';
import { useFeedbackSidebar } from '@/core/contexts/FeedbackSidebarContext';
import {
  createInitialFeedback,
  FeedbackDetailsStep,
  FeedbackRatingStep,
  type IFeedbackData,
  type IFeedbackFile,
} from './index';

type FeedbackStep = 'feedback' | 'details' | 'success';

export function Feedback() {
  const { t } = useTranslation();
  const { isOpen, setIsOpen } = useFeedbackSidebar();
  const [step, setStep] = useState<FeedbackStep>('feedback');
  const [feedback, setFeedback] = useState<IFeedbackData>(createInitialFeedback);
  const { user } = useAuth();
  const submitFeedbackMutation = useSubmitFeedback();
  const trackEvent = useTrackEvent();
  const { captureException } = useErrorCapture();
  const [location] = useLocation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Set context on open
  useEffect(() => {
    if (isOpen && step === 'feedback') {
      const hero = location.split('/')[1] || 'dashboard';
      setFeedback((prev) => ({
        ...prev,
        context: {
          page: location,
          hero: hero === 'dashboard' ? undefined : hero,
        },
      }));
    }
  }, [isOpen, location, step]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setStep('feedback');
        setFeedback(createInitialFeedback());
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen]);

  // Focus textarea on details step
  useEffect(() => {
    if (step === 'details' && textareaRef.current) {
      const timer = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step]);

  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const handleRatingSelect = useCallback((rating: number) => {
    setFeedback((prev) => ({ ...prev, rating }));
  }, []);

  const handleCategorySelect = useCallback((category: string) => {
    setFeedback((prev) => ({ ...prev, category }));
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setFeedback((prev) => ({ ...prev, description }));
  }, []);

  const handleFilesChange = useCallback((files: IFeedbackFile[]) => {
    setFeedback((prev) => ({ ...prev, files }));
  }, []);

  const handleContinueToDetails = useCallback(() => {
    if (!feedback.rating || !feedback.category) {
      toast.error(t('feedback.selectRatingAndCategory'));
      return;
    }
    setStep('details');
  }, [feedback.rating, feedback.category, t]);

  const handleBack = useCallback(() => setStep('feedback'), []);

  const handleSubmit = useCallback(async () => {
    if (!feedback.rating || !feedback.category || !feedback.description.trim()) {
      toast.error(t('feedback.validationError'));
      return;
    }

    const type =
      feedback.category === 'bug'
        ? 'bug'
        : feedback.category === 'suggestion'
          ? 'feature'
          : feedback.category === 'praise'
            ? 'improvement'
            : 'general';
    const subject =
      feedback.category === 'bug'
        ? t('feedback.category.bug.label')
        : feedback.category === 'suggestion'
          ? t('feedback.category.suggestion.label')
          : feedback.category === 'question'
            ? t('feedback.category.question.label')
            : feedback.category === 'praise'
              ? t('feedback.category.praise.label')
              : t('feedback.category.other.label');
    const pageUrl =
      typeof window !== 'undefined' ? window.location.href : (feedback.context?.page ?? '');

    try {
      await submitFeedbackMutation.mutateAsync({
        type,
        category: feedback.category,
        subject,
        message: feedback.description,
        pageUrl: pageUrl || undefined,
        rating: feedback.rating ?? undefined,
        portal: 'journey-web',
        // BL-022: attachments finally have a wire — multipart parts
        // forwarded into Sentry's feedback envelope server-side.
        files: feedback.files.map((f) => f.file),
      });

      trackEvent('FEEDBACK_SUBMITTED', {
        userId: user?.id,
        category: 'FEEDBACK',
        rating: feedback.rating,
        feedbackCategory: feedback.category,
        descriptionLength: feedback.description.length,
        fileCount: feedback.files.length,
      });

      setStep('success');
      toast.success(t('feedback.submitSuccess'));
    } catch (error) {
      captureException(error instanceof Error ? error : new Error(String(error)), {
        page: 'feedback',
        step: 'submit',
      });
      toast.error(t('feedback.submitError'));
    }
  }, [feedback, submitFeedbackMutation, trackEvent, user?.id, t, captureException]);

  const canContinue = feedback.rating !== null && feedback.category !== null;
  const canSubmit = !submitFeedbackMutation.isPending && feedback.description.trim().length > 0;

  return (
    <>
      {/* Spacer */}
      <div
        className={cn(
          'relative w-96 bg-transparent transition-[width] duration-200 ease-linear shrink-0',
          !isOpen && 'w-0'
        )}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <div
        className={cn(
          'flex fixed inset-y-0 right-0 z-40 h-screen w-96 border-l border-sidebar-border transition-[transform] duration-200 ease-linear',
          !isOpen && 'translate-x-full'
        )}
        role="dialog"
        data-testid="feedback-panel"
        aria-label={t('feedback.title')}
        aria-hidden={!isOpen}
        // `inert` removes the closed drawer's subtree from focus order —
        // aria-hidden alone leaves its buttons focusable (axe
        // aria-hidden-focus, surfaced when ENABLE_FEEDBACK went live).
        // Same pattern as the CoPilot drawer.
        inert={!isOpen}
      >
        <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground w-full">
          {/* Header */}
          <div className="border-b border-sidebar-border h-16">
            <div className="flex items-center justify-between gap-3 px-6 h-full">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-primary/10 border-2 border-primary/20">
                  <Lightbulb className="w-4 h-4 text-primary-accessible" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-sidebar-foreground leading-tight">
                    {t('feedback.title')}
                  </h2>
                  <p className="text-2xs text-muted-foreground leading-tight">
                    {t('feedback.subtitle')}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="size-9"
                aria-label={t('feedback.close')}
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-4 py-6 space-y-6">
              {step === 'success' && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-success-accessible" aria-hidden="true" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{t('feedback.thankYou')}</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('feedback.thankYouMessage')}
                  </p>
                  <Button
                    onClick={handleClose}
                    variant="outline"
                    data-testid="feedback-success-close"
                  >
                    {t('common.close')}
                  </Button>
                </div>
              )}

              {step === 'feedback' && (
                <FeedbackRatingStep
                  feedback={feedback}
                  onRatingSelect={handleRatingSelect}
                  onCategorySelect={handleCategorySelect}
                />
              )}

              {step === 'details' && (
                <FeedbackDetailsStep
                  feedback={feedback}
                  onDescriptionChange={handleDescriptionChange}
                  onFilesChange={handleFilesChange}
                  isSubmitting={submitFeedbackMutation.isPending}
                  textareaRef={textareaRef}
                />
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          {step !== 'success' && (
            <div className="px-4 py-4 border-t border-sidebar-border bg-sidebar">
              <div className="flex items-center gap-2">
                {step === 'feedback' ? (
                  <Button
                    onClick={handleContinueToDetails}
                    disabled={!canContinue}
                    className="flex-1"
                    data-testid="feedback-continue-button"
                  >
                    {t('common.continue')}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                      disabled={submitFeedbackMutation.isPending}
                    >
                      {t('common.back')}
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="flex-1"
                      data-testid="feedback-submit-button"
                    >
                      {submitFeedbackMutation.isPending ? (
                        <>
                          <div
                            className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"
                            aria-hidden="true"
                          />
                          {t('feedback.submitting')}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                          {t('feedback.submit')}
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
